import { deleteToken, getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";

import { firebaseApp } from "./firebase";
import * as SBService from "./sportbookService";

/**
 * Thông báo đẩy trên web, qua Firebase Cloud Messaging.
 *
 * Ba việc trong khối "Cần bạn thực hiện" chỉ hiện khi người dùng MỞ app. Bị
 * thách đấu lúc đang cất điện thoại thì không có gì báo - lời mời nằm đó tới
 * khi họ tình cờ mở lại.
 *
 * TUYỆT ĐỐI không tự xin quyền lúc vào app. Trình duyệt phạt rất nặng: người
 * dùng bấm "Chặn" một lần là chặn vĩnh viễn, không xin lại được, và Chrome còn
 * tự chặn luôn với những trang hay bị từ chối. Chỉ xin khi họ tự bật công tắc
 * ở trang Tài khoản.
 */

const VAPID = process.env.REACT_APP_FIREBASE_VAPID_KEY || "";

/**
 * Ghi lỗi thật ra console, kèm tên bước vừa hỏng.
 *
 * Người dùng chỉ thấy một câu ngắn; không có dòng log này thì khi họ chụp màn
 * hình gửi về, không ai đoán được hỏng ở Firebase hay ở máy chủ của mình.
 */
const than = (buoc: string, loi: unknown) => {
  // eslint-disable-next-line no-console
  console.error(`[thông báo đẩy] hỏng ở bước ${buoc}:`, loi);
};

/** Nhớ token đang dùng để lúc tắt còn biết xoá cái nào ở server. */
const KHOA_TOKEN = "biadi.pushToken";

export type LyDoKhongDuoc =
  | "trinh-duyet"
  | "chua-cai-pwa"
  | "chua-cau-hinh"
  | "bi-chan"
  | "khong-lay-duoc-ma"
  | "may-chu-tu-choi"
  | "that-bai";

export type KetQuaBat = { xong: true } | { xong: false; vi: LyDoKhongDuoc };

/**
 * App đang chạy ở dạng đã cài vào màn hình chính hay trong tab trình duyệt.
 *
 * iOS chỉ cho web push khi app ĐÃ ĐƯỢC CÀI vào màn hình chính (từ iOS 16.4).
 * Mở trong Safari thường thì `Notification` vẫn tồn tại nhưng xin quyền luôn
 * hỏng - nên phải tách riêng lý do này, không thì người dùng iPhone bấm bật,
 * thấy báo "không được" và chẳng biết phải làm gì.
 */
export const daCaiVaoManHinh = (): boolean =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  (window.navigator as any).standalone === true;

/**
 * Lời mời cài app của trình duyệt (Android/Chrome desktop).
 *
 * Trình duyệt bắn `beforeinstallprompt` MỘT LẦN, vào lúc nó thấy trang đủ điều
 * kiện - thường là ngay khi vào app, rất lâu trước khi người dùng bấm gì. Không
 * giữ lại ngay lúc đó thì sau này muốn mời cài cũng không còn gì để gọi.
 *
 * iOS KHÔNG có sự kiện này và không có API nào thay thế: Apple chỉ cho thêm vào
 * Màn hình chính bằng tay qua nút Chia sẻ. Nên trên iPhone chỉ hướng dẫn được.
 */
let loiMoiCai: any = null;

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    loiMoiCai = e;
  });
  window.addEventListener("appinstalled", () => {
    loiMoiCai = null;
  });
}

/** Trình duyệt này có mời cài được bằng một cú bấm không. */
export const coTheMoiCai = (): boolean => !!loiMoiCai;

/**
 * Hiện hộp thoại cài app của trình duyệt. Trả về true nếu người dùng đồng ý.
 *
 * Dùng một lần rồi bỏ: trình duyệt không cho gọi lại cùng một sự kiện.
 */
export const moiCaiApp = async (): Promise<boolean> => {
  if (!loiMoiCai) {
    return false;
  }
  const e = loiMoiCai;
  loiMoiCai = null;
  try {
    await e.prompt();
    const { outcome } = await e.userChoice;

    return outcome === "accepted";
  } catch {
    return false;
  }
};

/** Safari hay một trình duyệt khác trên iPhone - bước cài khác nhau. */
export const laSafariIOS = (): boolean =>
  laIOS() && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(navigator.userAgent);

export const laIOS = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  // iPadOS 13+ khai mình là Mac; phân biệt bằng cảm ứng.
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

/** Máy này về nguyên tắc nhận được thông báo đẩy không. */
export const hoTroDuoc = async (): Promise<boolean> => {
  if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
    return false;
  }

  return isSupported().catch(() => false);
};

/** Người dùng đã cho phép chưa: "default" | "granted" | "denied". */
export const quyenHienTai = (): NotificationPermission =>
  "Notification" in window ? Notification.permission : "denied";

/** Thiết bị này đang bật thông báo (đã có token đăng ký với server). */
export const dangBat = (): boolean => !!layTokenDaLuu();

const layTokenDaLuu = (): string | null => {
  try {
    return localStorage.getItem(KHOA_TOKEN);
  } catch {
    return null;
  }
};

const luuToken = (token: string | null) => {
  try {
    if (token) {
      localStorage.setItem(KHOA_TOKEN, token);
    } else {
      localStorage.removeItem(KHOA_TOKEN);
    }
  } catch {
    // Chế độ riêng tư chặn localStorage. Thông báo vẫn chạy trong phiên này.
  }
};

/**
 * Đăng ký service worker của Firebase.
 *
 * Truyền thẳng registration vào getToken thay vì để SDK tự tìm: SDK tự tìm thì
 * nó đăng ký ở đúng scope gốc, mà app này chạy ở gốc tên miền nên hai đường ra
 * cùng kết quả - nhưng làm rõ ở đây thì lúc trang phục vụ từ thư mục con vẫn
 * đúng, và lỗi đăng ký lộ ra ngay chứ không ẩn trong SDK.
 */
const dangKySW = async (): Promise<ServiceWorkerRegistration> =>
  navigator.serviceWorker.register("/firebase-messaging-sw.js", { scope: "/" });

/**
 * Bật thông báo cho thiết bị này.
 *
 * Gọi từ chính cú bấm của người dùng - `Notification.requestPermission` đòi một
 * "user gesture" ở vài trình duyệt.
 */
export const batThongBao = async (): Promise<KetQuaBat> => {
  if (!(await hoTroDuoc())) {
    /* Trên iPhone, chưa cài vào màn hình chính là hỏng chắc. Nói đúng lý do để
       người dùng còn biết đường làm, thay vì "máy bạn không hỗ trợ". */
    return { xong: false, vi: laIOS() && !daCaiVaoManHinh() ? "chua-cai-pwa" : "trinh-duyet" };
  }
  if (!VAPID) {
    // Thiếu khoá VAPID là lỗi cấu hình của bản build, không phải lỗi người dùng.
    return { xong: false, vi: "chua-cau-hinh" };
  }
  if (laIOS() && !daCaiVaoManHinh()) {
    return { xong: false, vi: "chua-cai-pwa" };
  }

  let quyen: NotificationPermission;
  try {
    quyen = await Notification.requestPermission();
  } catch (e) {
    than("xin quyền", e);

    return { xong: false, vi: "that-bai" };
  }
  if (quyen !== "granted") {
    return { xong: false, vi: "bi-chan" };
  }

  /* Hai bước sau hỏng vì hai lý do KHÁC HẲN nhau, và cách chữa cũng khác hẳn:
     lấy mã là chuyện giữa máy người dùng với Firebase, còn đăng ký là chuyện của
     máy chủ mình. Gộp cả hai vào một câu "kiểm tra mạng rồi thử lại" là đẩy người
     dùng đi sửa wifi trong khi lỗi nằm ở server. */
  let token: string;
  try {
    const registration = await dangKySW();
    token = await getToken(getMessaging(firebaseApp), {
      vapidKey: VAPID,
      serviceWorkerRegistration: registration,
    });
  } catch (e) {
    than("lấy mã thiết bị từ Firebase", e);

    return { xong: false, vi: "khong-lay-duoc-ma" };
  }
  if (!token) {
    than("lấy mã thiết bị từ Firebase", "Firebase trả về mã rỗng");

    return { xong: false, vi: "khong-lay-duoc-ma" };
  }

  try {
    await SBService.dangKyPushToken(token, navigator.userAgent);
  } catch (e: any) {
    than(`đăng ký mã với máy chủ (HTTP ${e?.response?.status ?? "?"})`, e);

    return { xong: false, vi: "may-chu-tu-choi" };
  }
  luuToken(token);

  return { xong: true };
};

/**
 * Tắt thông báo cho thiết bị này.
 *
 * Xoá ở server TRƯỚC rồi mới xoá ở máy: xoá ở máy trước mà lời gọi server hỏng
 * thì không còn biết token nào để báo, và server cứ gửi mãi cho một thiết bị đã
 * tắt - phải tới lượt gửi hỏng Firebase mới dọn giúp.
 */
export const tatThongBao = async (): Promise<void> => {
  const token = layTokenDaLuu();
  if (token) {
    await SBService.huyPushToken(token).catch(() => undefined);
  }
  try {
    if (await hoTroDuoc()) {
      await deleteToken(getMessaging(firebaseApp));
    }
  } catch {
    // Token đã chết sẵn cũng không sao - server đã xoá rồi.
  }
  luuToken(null);
};

/**
 * Người dùng đăng xuất: bỏ đăng ký thiết bị này.
 *
 * Không làm thì người tiếp theo đăng nhập trên cùng máy vẫn nhận thông báo của
 * người trước, cho tới khi họ tự bật thông báo (lúc đó server mới đổi chủ token).
 */
export const quenThietBiKhiDangXuat = async (): Promise<void> => {
  await tatThongBao().catch(() => undefined);
};

/**
 * Nghe thông báo khi app ĐANG MỞ.
 *
 * Service worker không chạy onBackgroundMessage trong trường hợp này, và trình
 * duyệt cũng không tự hiện gì - không nghe ở đây thì người đang mở app là người
 * duy nhất không được báo.
 */
export const ngheKhiDangMo = async (
  hien: (tin: { title: string; body: string; duongDan: string }) => void,
): Promise<() => void> => {
  if (!(await hoTroDuoc())) {
    return () => undefined;
  }
  try {
    return onMessage(getMessaging(firebaseApp), (payload) => {
      /* Đọc từ `data`, KHÔNG phải `notification`: backend gửi tin data-only nên
         payload.notification luôn rỗng. Đọc nhầm chỗ là thông báo hiện ra với
         tiêu đề "Biadi" và nội dung trống trơn. */
      const du = payload.data || {};
      hien({
        title: (du.title as string) || "Biadi",
        body: (du.body as string) || "",
        duongDan: (du.duongDan as string) || "/",
      });
    });
  } catch {
    return () => undefined;
  }
};
